import { useEffect } from 'react';

import ROUTES from '../config/routes';

export const useIntersectionObserver = (refs, setActiveSection) => {
  useEffect(() => {
    if (!refs || !setActiveSection) return;

    const elements = Object.values(refs)
      .map(ref => ref && ref.current)
      .filter(Boolean);
    if (elements.length === 0) return;

    let lastActive = null;
    const observer = new IntersectionObserver(
      entries => {
        if (window.__navScrollInProgress) return;

        let best = null;
        for (const entry of entries) {
          if (entry.intersectionRatio > 0) {
            if (!best || entry.intersectionRatio > best.intersectionRatio) {
              best = entry;
            }
          }
        }
        if (best && best.target && best.target.id && best.target.id !== lastActive) {
          lastActive = best.target.id;
          setActiveSection(best.target.id);
        }
      },
      {
        // Favor the section near the center of the viewport
        root: null,
        rootMargin: '-25% 0px -50% 0px',
        threshold: [0.1, 0.25, 0.5, 0.75]
      }
    );

    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [refs, setActiveSection]);
};

export const isMainRouteCheck = path => {
  const mainRoutes = [
    ROUTES.HOME,
    ROUTES.OCTOPUSSYNC,
    ROUTES.LOGIN,
    ROUTES.CONTACTS,
  ];
  const normalizedPath =
    path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
  return mainRoutes.includes(normalizedPath);
};

export const navigateToPathOrSection = (
  id,
  path,
  refs,
  navigate,
  currentPath,
  startPageTransition
) => {
  const targetPath = id ? `${path}#${id}` : path;
  const normalizedPath =
    path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;
  const normalizedCurrentPath =
    currentPath !== '/' && currentPath.endsWith('/')
      ? currentPath.slice(0, -1)
      : currentPath;

  if (normalizedPath !== normalizedCurrentPath) {
    if (startPageTransition && isMainRouteCheck(normalizedPath)) {
      startPageTransition(() => {
        navigate(targetPath);
      });
    } else {
      navigate(targetPath);
    }
  } else {
    if (id) {
      window.__navScrollInProgress = true;

      const element = refs && refs[id]?.current;
      if (element) {
        const offset = 100;
        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - offset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        if (window.location.hash !== `#${id}`) {
          navigate(targetPath, { replace: true });
        }

        setTimeout(() => {
          window.__navScrollInProgress = false;
        }, 200);
      } else {
        navigate(targetPath, { replace: true });
        setTimeout(() => {
          window.__navScrollInProgress = false;
        }, 200);
      }
    }
  }
};

export const getLogo = (textColor) => {
  // Return the appropriate horizontal logo based on text color
  if (textColor === '#ffffff' || textColor === 'white') {
    return '/logos/octopus-horizontal-white.svg';
  }
  return '/logos/octopus-horizontal-purple.svg';
};

export const getNavigationMenuItems = () => {
  return [
    {
      label: 'Home',
      path: ROUTES.HOME,
      hasDropdown: false
    },
    {
      label: 'OctopusSync',
      path: ROUTES.OCTOPUSSYNC,
      hasDropdown: true,
      submenu: [
        {
          label: 'About',
          path: `${ROUTES.OCTOPUSSYNC}#about`,
          id: 'about'
        },
        {
          label: 'Features',
          path: `${ROUTES.OCTOPUSSYNC}#features`,
          id: 'features'
        },
        {
          label: 'Comparison',
          path: `${ROUTES.OCTOPUSSYNC}#comparison`,
          id: 'comparison'
        }
      ]
    },
    {
      label: 'Login',
      path: ROUTES.LOGIN,
      hasDropdown: false
    },
    {
      label: 'Feature Request',
      path: '/feature-request',
      hasDropdown: false,
      isFeatureRequest: true
    },
    {
      label: 'Service',
      path: null,
      hasDropdown: false,
      isServiceStatus: true
    },
    {
      label: 'Contacts',
      path: ROUTES.CONTACTS,
      hasDropdown: false
    }
  ];
};

export const universalScrollTo = (position, duration = 800, behavior = 'smooth') => {
  window.scrollTo({
    top: position,
    behavior: behavior
  });
};
